import { DateTime } from "luxon";

import { dollars, dollarsToCents } from "../../../utils/money";

import type { BulkPaymentRowType } from "../bulkPayments/BulkPayment.form";

export const savedMappingToColumnMappings = (
  mapping: API.ACHImport.CSVFieldMapping,
): API.ACHImport.ColumnMapping[] => {
  const fields: API.ACHImport.AchField[] = [
    "date",
    "recipient_name",
    "routing_number",
    "account_number",
    "account_type",
    "action",
    "description",
    "amount",
  ];

  return fields.reduce<API.ACHImport.ColumnMapping[]>((acc, field) => {
    const index =
      mapping[`${field}_column_index` as keyof API.ACHImport.CSVFieldMapping];
    if (typeof index === "number") {
      const header =
        mapping[
          `${field}_column_header` as keyof API.ACHImport.CSVFieldMapping
        ];
      acc.push({
        field,
        column_index: index,
        column_header: typeof header === "string" ? header : null,
      });
    }
    return acc;
  }, []);
};

export const columnMappingsToSavedMapping = (
  name: string,
  hasHeaderRow: boolean,
  columnMappings: API.ACHImport.ColumnMapping[],
): API.ACHImport.CSVFieldMappingRequest => {
  const mapping: API.ACHImport.CSVFieldMappingRequest = {
    name,
    has_header_row: hasHeaderRow,
    date_column_index: null,
    date_column_header: null,
    recipient_name_column_index: null,
    recipient_name_column_header: null,
    routing_number_column_index: null,
    routing_number_column_header: null,
    account_number_column_index: null,
    account_number_column_header: null,
    account_type_column_index: null,
    account_type_column_header: null,
    action_column_index: null,
    action_column_header: null,
    description_column_index: null,
    description_column_header: null,
    amount_column_index: null,
    amount_column_header: null,
  };

  columnMappings.forEach((cm) => {
    const indexKey =
      `${cm.field}_column_index` as keyof API.ACHImport.CSVFieldMappingRequest;
    const headerKey =
      `${cm.field}_column_header` as keyof API.ACHImport.CSVFieldMappingRequest;
    (mapping[indexKey] as number | null) = cm.column_index;
    (mapping[headerKey] as string | null) = cm.column_header;
  });

  return mapping;
};

export const parsedRowsToBulkPayments = (
  rows: API.ACHImport.CSVParsedRow[],
  defaultScheduledDate: string,
  defaultDescription = "",
): BulkPaymentRowType[] => {
  return rows
    .filter((row) => row.row_errors.length === 0)
    .map((row) => {
      const amountCents = dollarsToCents(dollars(row.amount));
      const scheduledDate = row.scheduled_date
        ? DateTime.fromISO(row.scheduled_date)
        : null;

      return {
        recipient: row.recipient ?? undefined,
        description: row.description ?? defaultDescription,
        amount: Number.isNaN(amountCents) ? 0 : amountCents,
        transactionType: row.transaction_type,
        scheduledDate: scheduledDate?.isValid
          ? scheduledDate.toFormat("MM/dd/yyyy")
          : defaultScheduledDate,
      };
    });
};

export const getMissingRequiredFields = (
  columnMappings: API.ACHImport.ColumnMapping[],
): API.ACHImport.AchField[] => {
  const REQUIRED_FIELDS: API.ACHImport.AchField[] = [
    "recipient_name",
    "account_number",
    "routing_number",
    "amount",
  ];
  const mappedFields = new Set(columnMappings.map((cm) => cm.field));
  return REQUIRED_FIELDS.filter((f) => !mappedFields.has(f));
};

export const getPreviewValue = (
  previewRows: string[][],
  columnIndex: number,
  previewRowIndex: number,
): string => {
  const row = previewRows[previewRowIndex];
  if (!row) return "";
  return row[columnIndex] ?? "";
};
